import Column from '@app/components/layout/Column'
import { TextField } from '@app/components/core/form/fields/TextField'
import { Text } from '@app/components/core/typography/Text'
import { YesNoToggle } from '@app/components/core/toggle/YesNoToggle'
import { YesNoOtherToggle } from '@app/components/core/toggle/YesNoOtherToggle'
import { PLOT_STATUS } from '@schema/initiative'
import { useTranslations } from '@i18n/useLanguage'
import { type ReactNode, useEffect } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import type { LegalInfoFormValues } from '@app/pages/dashboard/initiative/shared/validation/initiativeValidation'

const labelStyle = {
  color: 'var(--brand-secondary)',
  fontWeight: 500,
} as const

export const LegalInfoFormInitialSync = ({
  initial,
}: {
  initial: LegalInfoFormValues
}): ReactNode => {
  const { reset, trigger } = useFormContext<LegalInfoFormValues>()
  useEffect(() => {
    reset(initial)
    void trigger()
  }, [initial, reset, trigger])
  return null
}

export const LegalInfoFormFields = () => {
  const { control, trigger } = useFormContext<LegalInfoFormValues>()
  const creation = useTranslations().app.dashboard.initiative.creation

  const potentialPlotStatus = useWatch({
    control,
    name: 'potentialPlotStatus',
  })
  const renderPlotQuestions = potentialPlotStatus !== PLOT_STATUS.noPlot

  useEffect(() => {
    void trigger('potentialPlotAddress')
  }, [potentialPlotStatus, trigger])

  return (
    <Column gap="2.5rem" alignItems="stretch">
      <Column gap="0.5rem">
        <Text.Medium isBold={true}>{creation.plotUnderOption}</Text.Medium>
        <Controller
          name="potentialPlotStatus"
          control={control}
          render={({ field }) => (
            <YesNoOtherToggle
              optionValues={{
                yes: PLOT_STATUS.underOption,
                no: PLOT_STATUS.noPlot,
                other: PLOT_STATUS.potentialPlotInMind,
              }}
              otherLabel={creation.notUnderOptionButInMind}
              selected={field.value}
              setSelected={field.onChange}
            />
          )}
        />
      </Column>
      {renderPlotQuestions && (
        <TextField<LegalInfoFormValues>
          name="potentialPlotAddress"
          label={creation.address}
          required={true}
          labelStyle={labelStyle}
        />
      )}
      {renderPlotQuestions && (
        <Column gap="0.5rem">
          <Text.Medium isBold={true}>{creation.permit}</Text.Medium>
          <Controller
            name="permit"
            control={control}
            render={({ field }) => (
              <YesNoToggle
                yesSelected={field.value}
                setYesSelected={field.onChange}
              />
            )}
          />
        </Column>
      )}
      <Column gap="0.5rem">
        <Text.Medium isBold={true}>
          {creation.financingRequirementsConstruction}
        </Text.Medium>
        <Controller
          name="financingRequirementsConstruction"
          control={control}
          render={({ field }) => (
            <YesNoToggle
              yesSelected={field.value}
              setYesSelected={field.onChange}
            />
          )}
        />
      </Column>
      <Column gap="0.5rem">
        <Text.Medium isBold={true}>
          {creation.financingRequirementsPlanning}
        </Text.Medium>
        <Controller
          name="financingRequirementsPlanning"
          control={control}
          render={({ field }) => (
            <YesNoToggle
              yesSelected={field.value}
              setYesSelected={field.onChange}
            />
          )}
        />
      </Column>
      <Column gap="0.5rem">
        <Text.Medium isBold={true}>{creation.kvkRegistration}</Text.Medium>
        <Controller
          name="kvkRegistration"
          control={control}
          render={({ field }) => (
            <YesNoToggle
              yesSelected={field.value}
              setYesSelected={field.onChange}
            />
          )}
        />
      </Column>
    </Column>
  )
}
